import { AgeType } from "libs/cat-calculations/src/lib/cat-calculations.types";
import { Recipe, RecipesForCatResponse } from "@forms/schema";
import { IRecipeItem } from "libs/state/src/lib/stores/signupFlow/shared/types";

import { Allergen } from "@/primary-models";

export type RecipeCount = 7 | 14 | 28;

type RecipeAllocation = Partial<Record<Recipe, number>>;
type AllergyScenario = "NO_ALLERGY" | "BEEF" | "TURKEY" | "DUCK" | "CHICKEN" | "SALMON" | "CHICKEN_FISH";
const BASE_RECIPES: Recipe[] = [ "BAAA", "CLUCK", "GOBBLE", "MOOO", "OINK", "QUACK", "SPLASH" ];
const SHREDDED_RECIPES: Recipe[] = [ "SHREDDED_CLUCK", "SHREDDED_GOBBLE" ];

export const getAvailableFreshRecipes = (isShreddedEnabled = false): Recipe[] => (
  isShreddedEnabled
    ? [ ...BASE_RECIPES, ...SHREDDED_RECIPES ]
    : BASE_RECIPES
);

/**
 * Recipe allocations organised by tray size, then by allergy scenario.
 *
 * When shredded recipes are enabled, shredded variants follow the same allergy rules as
 * their base recipes. The table below lists excluded recipes including shredded variants
 * where applicable (e.g. `SHREDDED_CLUCK` is treated like `CLUCK` for allergy purposes).
 *
 * | Allergy Scenario | Excluded Recipes                                                | Available Recipes           |
 * |------------------|-----------------------------------------------------------------|-----------------------------|
 * | None             | -                                                               | CLUCK, GOBBLE, QUACK, MOOO |
 * | Pork             | OINK                                                            | Fast-path (no effect)       |
 * | Lamb             | BAAA                                                            | Fast-path (no effect)       |
 * | Whitefish        | SPLASH                                                          | Fast-path (no effect)       |
 * | Beef             | MOOO, BAAA                                                      | CLUCK, GOBBLE, QUACK        |
 * | Turkey           | GOBBLE, SHREDDED_GOBBLE, SPLASH                                 | CLUCK, QUACK, MOOO          |
 * | Duck             | QUACK                                                           | CLUCK, GOBBLE, MOOO         |
 * | Chicken          | CLUCK, SHREDDED_CLUCK, GOBBLE, SHREDDED_GOBBLE, QUACK          | MOOO, OINK, SPLASH          |
 * | Salmon           | SPLASH, QUACK, MOOO, BAAA, OINK, SHREDDED_CLUCK, SHREDDED_GOBBLE | CLUCK, GOBBLE             |
 * | Chicken+Fish     | CLUCK, SHREDDED_CLUCK, GOBBLE, SHREDDED_GOBBLE, QUACK, SPLASH  | MOOO only                   |
 *
 */
const ALLOCATIONS: Record<RecipeCount, Record<AllergyScenario, RecipeAllocation>> = {
  7: {
    NO_ALLERGY: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
    BEEF: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
    TURKEY: { CLUCK: 4, QUACK: 2, MOOO: 1 },
    DUCK: { CLUCK: 4, GOBBLE: 2, MOOO: 1 },
    CHICKEN: { OINK: 4, MOOO: 2, SPLASH: 1 },
    SALMON: { CLUCK: 4, GOBBLE: 3 },
    CHICKEN_FISH: { MOOO: 7 },
  },
  14: {
    NO_ALLERGY: { CLUCK: 7, GOBBLE: 3, QUACK: 2, MOOO: 2 },
    BEEF: { CLUCK: 7, GOBBLE: 4, QUACK: 3 },
    TURKEY: { CLUCK: 7, QUACK: 4, MOOO: 3 },
    DUCK: { CLUCK: 7, GOBBLE: 4, MOOO: 3 },
    CHICKEN: { MOOO: 7, OINK: 4, SPLASH: 3 },
    SALMON: { CLUCK: 9, GOBBLE: 5 },
    CHICKEN_FISH: { MOOO: 14 },
  },
  28: {
    NO_ALLERGY: { CLUCK: 14, GOBBLE: 6, QUACK: 4, MOOO: 4 },
    BEEF: { CLUCK: 14, GOBBLE: 8, QUACK: 6 },
    TURKEY: { CLUCK: 14, QUACK: 8, MOOO: 6 },
    DUCK: { CLUCK: 14, GOBBLE: 8, MOOO: 6 },
    CHICKEN: { MOOO: 14, OINK: 8, SPLASH: 6 },
    SALMON: { CLUCK: 14, GOBBLE: 14 },
    CHICKEN_FISH: { MOOO: 28 },
  },
};

/**
 * Default recipe distributions for each tray size (no allergies).
 */
const DEFAULT_RECIPES: Record<RecipeCount, RecipeAllocation> = {
  7: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
  14: { CLUCK: 7, GOBBLE: 3, MOOO: 2, QUACK: 2 },
  28: { CLUCK: 14, GOBBLE: 6, MOOO: 4, QUACK: 4 },
};

const SHREDDED_ALLOCATIONS: Record<RecipeCount, Record<AllergyScenario, RecipeAllocation>> = {
  7: ALLOCATIONS[7],
  14: {
    NO_ALLERGY: { CLUCK: 4, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 1, QUACK: 2, MOOO: 2 },
    BEEF: { CLUCK: 4, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 2, QUACK: 3 },
    TURKEY: { CLUCK: 4, SHREDDED_CLUCK: 3, QUACK: 4, MOOO: 3 },
    DUCK: { CLUCK: 4, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 2, MOOO: 3 },
    CHICKEN: { MOOO: 7, OINK: 4, SPLASH: 3 },
    SALMON: { CLUCK: 9, GOBBLE: 5 },
    CHICKEN_FISH: { MOOO: 14 },
  },
  28: {
    NO_ALLERGY: { CLUCK: 8, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 2, QUACK: 4, MOOO: 4 },
    BEEF: { CLUCK: 8, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 4, QUACK: 6 },
    TURKEY: { CLUCK: 8, SHREDDED_CLUCK: 6, QUACK: 8, MOOO: 6 },
    DUCK: { CLUCK: 8, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 4, MOOO: 6 },
    CHICKEN: { MOOO: 14, OINK: 8, SPLASH: 6 },
    SALMON: { CLUCK: 18, GOBBLE: 10 },
    CHICKEN_FISH: { MOOO: 28 },
  },
};

const SHREDDED_DEFAULT_RECIPES: Record<RecipeCount, RecipeAllocation> = {
  7: DEFAULT_RECIPES[7],
  14: { CLUCK: 4, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 1, MOOO: 2, QUACK: 2 },
  28: { CLUCK: 8, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 2, MOOO: 4, QUACK: 4 },
};

export const getDisallowedRecipes = (lifeStage: AgeType | undefined): Recipe[] => {
  if (lifeStage === "kitten") {
    return [ "BAAA" ];
  }
  return [];
};

export const transformRecipeItemsToRecipeMap = (
  recipeItems: IRecipeItem[],
  isShreddedEnabled = false,
): Map<Recipe, number> => {
  const recipeMap = new Map<Recipe, number>();
  const availableRecipes = new Set(getAvailableFreshRecipes(isShreddedEnabled));
  recipeItems.forEach((item) => {
    if (!availableRecipes.has(item.type)) {
      return;
    }
    recipeMap.set(item.type, item.quantity);
  });

  return recipeMap;
};

export const getDefaultRecipes = (recipeCount: RecipeCount = 14, isShreddedEnabled = false): Map<Recipe, number> => {
  const recipeMap = new Map<Recipe, number>();
  const defaults = isShreddedEnabled ? SHREDDED_DEFAULT_RECIPES[recipeCount] : DEFAULT_RECIPES[recipeCount];

  Object.entries(defaults).forEach(([ recipe, quantity ]) => {
    if (quantity !== undefined && quantity > 0) {
      recipeMap.set(recipe as Recipe, quantity);
    }
  });

  return recipeMap;
};

export const getFreshTrialRecipes = (
  allergies: Allergen[],
  lifeStage: AgeType,
  recipesItems: IRecipeItem[] | undefined = undefined,
  recipeCount: RecipeCount = 14,
  isShreddedEnabled = false,
): RecipesForCatResponse => {
  const recipes = recipesItems
    ? transformRecipeItemsToRecipeMap(recipesItems, isShreddedEnabled)
    : getDefaultRecipes(recipeCount, isShreddedEnabled);
  return calculateRecipesForCat(recipes, allergies, getDisallowedRecipes(lifeStage), recipeCount, isShreddedEnabled);
};

export const calculateRecipesForCat = (
  defaultRecipeWeightings: Map<Recipe, number>,
  allergies: Allergen[],
  ignoreRecipes: Recipe[],
  recipeCount: RecipeCount = 14,
  isShreddedEnabled = false,
): RecipesForCatResponse => {
  const recipesNotApplicableForCat = allergies?.map((allergy) => allergyRecipeRecord[allergy]);
  const reduced = recipesNotApplicableForCat?.length > 0
    ? recipesNotApplicableForCat.reduce<Set<Recipe>>(
      (prev, cur) => new Set([ ...cur, ...prev ]),
      new Set<Recipe>(),
    )
    : new Set<Recipe>();
  ignoreRecipes.forEach((ir) => reduced.add(ir));
  const recipeQuantities = calculateRecipeQuantities(
    defaultRecipeWeightings,
    reduced,
    recipeCount,
    isShreddedEnabled,
  );

  const result = {
    allowedRecipesAndQuantities: recipeQuantities,
    allergicRecipes: reduced,
    ignoreRecipes,
  };

  const recipesMap: { [ recipe: string ]: number } = {};
  [ ...result.allowedRecipesAndQuantities.keys() ].forEach((recipe) => {
    const quantity = result.allowedRecipesAndQuantities.get(recipe) || 0;
    if (quantity > 0) {
      recipesMap[recipe] = quantity;
    }
  });

  const allergyArr = Array.from(result.allergicRecipes);
  return {
    allowedRecipesAndQuantities: recipesMap,
    allergicRecipes: allergyArr,
    notAllowedRecipes: result.ignoreRecipes,
    inDefaultRecipeTest: "control",
  };
};

export const calculateRecipeQuantities = (
  defaultRecipes: Map<Recipe, number>,
  ignoreRecipes: Set<Recipe>,
  recipeCount: RecipeCount,
  isShreddedEnabled = false,
): Map<Recipe, number> => {
  // Fast path: no allergies affecting default recipes
  if (!ignoreRecipes || ignoreRecipes.size === 0 ||
       (!([ ...defaultRecipes.keys() ].some((r) => (defaultRecipes.get(r) || 0) > 0 && ignoreRecipes.has(r))))) {
    const toBeReturned: Map<Recipe, number> = new Map();
    defaultRecipes.forEach((quant, recipe) => {
      if (!ignoreRecipes.has(recipe)) {
        toBeReturned.set(recipe, quant);
      } else {
        toBeReturned.delete(recipe);
      }
    });
    return toBeReturned;
  }

  const allocations = isShreddedEnabled ? SHREDDED_ALLOCATIONS : ALLOCATIONS;

  // Initialise all recipes with zero, then remove ignored ones
  const recipesToReturn = new Map<Recipe, number>();
  getAvailableFreshRecipes(isShreddedEnabled).forEach((recipe) => recipesToReturn.set(recipe, 0));
  ignoreRecipes.forEach((toBeRemoved) => {
    recipesToReturn.delete(toBeRemoved);
  });

  // Detect allergy scenario and apply allocation
  const scenario = detectAllergyScenario(ignoreRecipes);
  if (scenario !== "FALLBACK") {
    const allocation = allocations[recipeCount][scenario];
    Object.entries(allocation).forEach(([ recipe, quantity ]) => {
      if (quantity !== undefined) {
        recipesToReturn.set(recipe as Recipe, quantity);
      }
    });
    return recipesToReturn;
  }

  // Fallback: distribute evenly across available recipes
  const allRecipes = getAvailableFreshRecipes(isShreddedEnabled);
  const availableRecipes = allRecipes.filter((r) => !ignoreRecipes.has(r));
  const totalWeight = availableRecipes.length;

  if (totalWeight === 0) {
    return recipesToReturn;
  }

  let remaining = recipeCount;
  availableRecipes.forEach((recipe) => {
    const quantityToAdd = Math.floor(recipeCount / totalWeight);
    recipesToReturn.set(recipe, quantityToAdd);
    remaining -= quantityToAdd;
  });

  if (remaining > 0) {
    const firstRecipe = availableRecipes[0];
    recipesToReturn.set(firstRecipe, (recipesToReturn.get(firstRecipe) ?? 0) + remaining);
  }

  return recipesToReturn;
};

/**
 * Scenario detection rules: each scenario requires specific recipes to be available.
 * Order matters - first match wins (more specific scenarios should come first).
 */
const SCENARIO_RULES: Array<{ scenario: AllergyScenario; requiredRecipes: Recipe[] }> = [
  { scenario: "NO_ALLERGY", requiredRecipes: [ "CLUCK", "GOBBLE", "QUACK", "MOOO" ] },
  { scenario: "BEEF", requiredRecipes: [ "CLUCK", "GOBBLE", "QUACK" ] },
  { scenario: "TURKEY", requiredRecipes: [ "CLUCK", "QUACK", "MOOO" ] },
  { scenario: "DUCK", requiredRecipes: [ "CLUCK", "GOBBLE", "MOOO" ] },
  { scenario: "CHICKEN", requiredRecipes: [ "MOOO", "SPLASH", "OINK" ] },
  { scenario: "SALMON", requiredRecipes: [ "CLUCK", "GOBBLE" ] },
  { scenario: "CHICKEN_FISH", requiredRecipes: [ "MOOO" ] },
];

/**
 * Detects which allergy scenario applies based on ignored recipes.
 */
const detectAllergyScenario = (ignoreRecipes: Set<Recipe>): AllergyScenario | "FALLBACK" => {
  const isAvailable = (recipe: Recipe) => !ignoreRecipes.has(recipe);

  for (const rule of SCENARIO_RULES) {
    if (rule.requiredRecipes.every(isAvailable)) {
      return rule.scenario;
    }
  }

  return "FALLBACK";
};

export const allergyRecipeRecord: Record<Allergen, Set<Recipe>> = {
  CHICKEN: new Set([ "CLUCK", "QUACK", "GOBBLE", "SHREDDED_CLUCK", "SHREDDED_GOBBLE" ]),
  WHITEFISH: new Set([ "SPLASH" ]),
  SALMON: new Set([ "SPLASH", "QUACK", "MOOO", "BAAA", "OINK", "SHREDDED_CLUCK", "SHREDDED_GOBBLE" ]),
  PORK: new Set([ "OINK" ]),
  BEEF: new Set([ "MOOO", "BAAA" ]),
  TURKEY: new Set([ "GOBBLE", "SPLASH", "SHREDDED_GOBBLE" ]),
  DUCK: new Set([ "QUACK" ]),
  LAMB: new Set([ "BAAA" ]),
  TUNA: new Set(),
};
